import React, { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import Button from '../UI/Button';
import { clientService } from '../../services/clientService';
import { dietService } from '../../services/dietService';
import { routeDeliveryService } from '../../services/routeDeliveryService';
import { paymentService } from '../../services/paymentService';
import { Client, CreateClientRequest, UpdateClientRequest, ClientStatus, City, Country, getBMI, getIBW, getUBW, getCalories } from '../../types/client';
import { Diet, Price } from '../../types/diet';
import MedicalHistoryModal from './MedicalHistoryModal';
import RecipeSelectionModal from './RecipeSelectionModal';

interface Props {
  client?: Client | null;
  onClose: () => void;
  onSuccess: () => void;
}

const STEPS = [
  { id: 1, name: 'Client Personal Info', label: '1. Client General Info' },
  { id: 2, name: 'Assessment Info', label: '2. Assessment Info' },
  { id: 3, name: 'Diet & Menu', label: '3. Diet & Menu' },
  { id: 4, name: 'Payments', label: '4. Payments' },
];

interface Cycle {
  id: string;
  sequence: number;
  cycleNumber: number;
  fromDate: string;
  toDate: string;
  selected: boolean;
  recipes: string; // JSON string or comma-separated recipe IDs
  paid: boolean;
  freezedNotes: string;
  status: 'Active' | 'Inactive';
  statusDate: string;
}

const ClientModal: React.FC<Props> = ({ client, onClose, onSuccess }) => {
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [showMedicalHistoryModal, setShowMedicalHistoryModal] = useState(false);
  const isEditing = !!client;

  const { data: citiesData } = useQuery({
    queryKey: ['cities', 'for-select'],
    queryFn: () => clientService.getCities({ page: 1, limit: 1000 }),
  });
  const { data: countriesData } = useQuery({
    queryKey: ['countries', 'for-select'],
    queryFn: () => clientService.getCountries({ page: 1, limit: 1000 }),
  });
  const { data: dietsData } = useQuery({
    queryKey: ['diets', 'for-select'],
    queryFn: () => dietService.getDiets({ page: 1, limit: 1000 }),
  });
  const { data: pricesData } = useQuery({
    queryKey: ['prices', 'for-select'],
    queryFn: () => dietService.getPrices({ page: 1, limit: 1000 }),
  });
  const { data: ingredientsData } = useQuery({
    queryKey: ['ingredients', 'for-select'],
    queryFn: () => clientService.getIngredients({ limit: 1000 }),
  });
  const { data: routeDeliveriesData } = useQuery({
    queryKey: ['route-deliveries', 'for-select'],
    queryFn: () => routeDeliveryService.getRouteDeliveries({ page: 1, limit: 1000, status: 'ACTIVE' }),
  });

  const { register, handleSubmit, formState: { errors }, reset, watch, setValue, control } = useForm<CreateClientRequest & { status?: ClientStatus, countryId?: string, zipCode?: string, motherFullName?: string }>({
    defaultValues: {
      firstName: client?.firstName || '',
      lastName: client?.lastName || '',
      fatherName: (client as any)?.fatherName || '',
      motherFullName: (client as any)?.motherFullName || '',
      email: client?.email || '',
      mobilePhone: (client as any)?.mobilePhone || client?.phone || '',
      countryId: (client as any)?.nationality?.id || '',
      nationalityId: (client as any)?.nationality?.id || '',
      locationId: (client as any)?.locationId || (client as any)?.location?.id || client?.cityId || '',
      dietId: (client as any)?.diet?.id || '',
      status: client?.status || 'PENDING',
      dateOfBirth: (client as any)?.dateOfBirth ? new Date((client as any).dateOfBirth).toISOString().split('T')[0] : '',
      gender: (client as any)?.gender || '',
      height: (client as any)?.height || '',
      weight: (client as any)?.weight || '',
      activityLevel: (client as any)?.activityLevel || '',
      medicalConditions: (client as any)?.medicalConditions || '',
      allergies: (client as any)?.allergies || '',
      dietaryPreferences: (client as any)?.dietaryPreferences || '',
      age: (client as any)?.age || '',
      bmiStatus: (client as any)?.bmiStatus || '',
      weightChange: (client as any)?.weightChange || '',
      medicalHistory: (client as any)?.medicalHistory || '',
      medications: (client as any)?.medications || '',
      vitaminDeficiencies: (client as any)?.vitaminDeficiencies || '',
      counterMedications: (client as any)?.counterMedications || '',
      previousDiet: (client as any)?.previousDiet || '',
      lastDietDate: (client as any)?.lastDietDate ? new Date((client as any).lastDietDate).toISOString().split('T')[0] : '',
      weightLost: (client as any)?.weightLost || '',
      smoking: (client as any)?.smoking || false,
      alcohol: (client as any)?.alcohol || false,
      allergiesHistory: (client as any)?.allergiesHistory || '',
      foodDislike: (client as any)?.foodDislike || '',
      eatingDisorder: (client as any)?.eatingDisorder || false,
      zipCode: (client as any)?.zipCode || '',
    }
  });

  // Watch height, weight, gender, dateOfBirth, activityLevel, and countryId for auto-calculation and location filtering
  const height = useWatch({ control, name: 'height' });
  const weight = useWatch({ control, name: 'weight' });
  const gender = useWatch({ control, name: 'gender' });
  const priceId = useWatch({ control, name: 'priceId' as any });

  // Diet & Menu (Step 3) transient UI state (not sent to API)
  const [nbDays, setNbDays] = useState<number>(30);
  const [fullPackage, setFullPackage] = useState<boolean>(false);
  const [dailyPrice, setDailyPrice] = useState<string>('0.00');
  const [startingDate, setStartingDate] = useState<string>('');
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [showRecipeSelectionModal, setShowRecipeSelectionModal] = useState(false);
  const [selectedCycleForRecipes, setSelectedCycleForRecipes] = useState<Cycle | null>(null);

  // Payment (Step 4) transient UI state
  const [totalPrice, setTotalPrice] = useState<string>('0.00');
  const [withDelivery, setWithDelivery] = useState<boolean>(false);
  const [withTax, setWithTax] = useState<boolean>(false);
  const [deliveryAddresses, setDeliveryAddresses] = useState<Array<{
    addressType: string;
    addressPreference: string;
    location: string;
    deliveryCharge: string;
    daysRelated: string[];
    routeDelivery: string;
    time: string;
    whenNoOneInHome: string;
    reference: string;
    deliveredBy: string;
  }>>([{
    addressType: '',
    addressPreference: '',
    location: '',
    deliveryCharge: '0.00',
    daysRelated: [],
    routeDelivery: '',
    time: '',
    whenNoOneInHome: '',
    reference: '',
    deliveredBy: '',
  }]);

  // Client Full Address list (UI-only for now)
  const [addresses, setAddresses] = useState<string[]>(['']);

  const dateOfBirth = useWatch({ control, name: 'dateOfBirth' });
  const activityLevel = useWatch({ control, name: 'activityLevel' });
  const countryId = useWatch({ control, name: 'countryId' });
  const medicalHistoryValue = useWatch({ control, name: 'medicalHistory' as any });

  const medicalHistorySummary = React.useMemo(() => {
    try {
      if (!medicalHistoryValue) return '';
      const arr = JSON.parse(medicalHistoryValue as any);
      if (!Array.isArray(arr)) return '';
      const selected = arr.filter((i: any) => i && i.checked).map((i: any) => i.name).filter(Boolean);
      const noted = arr.filter((i: any) => (i?.description && String(i.description).trim()) || (i?.otherDescription && String(i.otherDescription).trim())).length;
      const parts: string[] = [];
      if (selected.length) parts.push(`${selected.length} selected: ${selected.slice(0, 4).join(', ')}${selected.length > 4 ? '…' : ''}`);
      if (noted) parts.push(`${noted} with notes`);
      return parts.join(' • ');
    } catch {
      return '';
    }
  }, [medicalHistoryValue]);

  // Auto-set nationality and phone prefix when country is selected
  useEffect(() => {
    console.log('Effect running - countryId:', countryId, 'countriesData:', countriesData);
    if (countryId && countriesData?.countries) {
      setValue('nationalityId', countryId);

      // When country changes, clear location if it doesn't belong to selected country
      const validCities = (citiesData?.cities || []).filter((c: City) => (c as any).countryId === countryId);
      const currentLocationId = watch('locationId' as any);
      if (currentLocationId && !validCities.some((c) => c.id === currentLocationId)) {
        setValue('locationId', '');
      }

      // Find the selected country and auto-fill phone prefix
      const selectedCountry = countriesData.countries.find((c: Country) => c.id === countryId);
      console.log('Selected country:', selectedCountry);
      console.log('Selected country keys:', selectedCountry ? Object.keys(selectedCountry) : 'N/A');
      console.log('Full countriesData:', JSON.stringify(countriesData, null, 2));
      if (selectedCountry && selectedCountry.phonePrefix) {
        console.log('Setting phone prefix:', selectedCountry.phonePrefix);
        setValue('mobilePhone', selectedCountry.phonePrefix);
      }
    }
  }, [countryId, setValue, countriesData, citiesData, watch]);

  // Auto-calculate health metrics
  useEffect(() => {
    const heightNum = height ? parseFloat(height as any) : undefined;
    const weightNum = weight ? parseFloat(weight as any) : undefined;

    if (heightNum && weightNum) {
      const bmi = getBMI(heightNum, weightNum);
      const ibw = getIBW(heightNum, gender as any);
      const ubw = getUBW(weightNum);
      const calories = getCalories(weightNum, heightNum, dateOfBirth, gender as any, activityLevel as any);

      if (bmi) setValue('bmi' as any, bmi);
      if (ibw) setValue('ibw' as any, ibw);
      if (ubw) setValue('ubw' as any, ubw);
      if (calories) setValue('calories' as any, calories);
    }
  }, [height, weight, gender, dateOfBirth, activityLevel, setValue]);

  // Auto-update Daily Price when Price is selected
  useEffect(() => {
    if (priceId && pricesData?.prices) {
      const selectedPrice = pricesData.prices.find((p: Price) => p.id === priceId);
      if (selectedPrice) {
        console.log('[ClientModal] Selected price:', selectedPrice);
        console.log('[ClientModal] Setting daily price to:', selectedPrice.amount);
        setDailyPrice(selectedPrice.amount.toString());
      }
    } else {
      setDailyPrice('0.00');
    }
  }, [priceId, pricesData]);

  useEffect(() => {
    if (client) {
      reset({
        firstName: client.firstName,
        lastName: client.lastName,
        fatherName: (client as any)?.fatherName || '',
        motherFullName: (client as any)?.motherFullName || '',
        email: client.email || '',
        mobilePhone: (client as any).mobilePhone || client.phone || '',
        nationalityId: (client as any)?.nationality?.id || (client as any)?.nationalityId || '',
        locationId: (client as any)?.locationId || (client as any)?.location?.id || client.cityId || '',
        dietId: (client as any)?.diet?.id || (client as any)?.dietId || '',
        priceId: (client as any)?.price?.id || (client as any)?.priceId || '',
        status: client.status as ClientStatus,
        dateOfBirth: (client as any)?.dateOfBirth ? new Date((client as any).dateOfBirth).toISOString().split('T')[0] : '',
        gender: (client as any)?.gender || '',
        height: (client as any)?.height || '',
        weight: (client as any)?.weight || '',
        activityLevel: (client as any)?.activityLevel || '',
        medicalConditions: (client as any)?.medicalConditions || '',
        allergies: (client as any)?.allergies || '',
        dietaryPreferences: (client as any)?.dietaryPreferences || '',
        // Load calculated metrics from client data
        bmi: (client as any)?.bmi || '',
        ibw: (client as any)?.ibw || '',
        ubw: (client as any)?.ubw || '',
        calories: (client as any)?.calories || '',
        bmiStatus: (client as any)?.bmiStatus || '',
        weightChange: (client as any)?.weightChange || '',
      });
      setAddresses(((client as any)?.addresses || []).map((a: any) => a.fullAddress) || ['']);
      // Set daily price from client data
      if ((client as any)?.price?.amount) {
        setDailyPrice((client as any).price.amount.toString());
      }
      // Load cycles and starting date from client data
      if ((client as any)?.startingDate) {
        setStartingDate(new Date((client as any).startingDate).toISOString().split('T')[0]);
      }
      if ((client as any)?.cycles) {
        try {
          const parsedCycles = JSON.parse((client as any).cycles);
          setCycles(Array.isArray(parsedCycles) ? parsedCycles : []);
        } catch {
          setCycles([]);
        }
      }
      // Load delivery flag for "With Delivery" checkbox
      if ((client as any)?.delivery !== undefined) {
        setWithDelivery(!!(client as any).delivery);
      } else {
        setWithDelivery(false);
      }

      // Load delivery information (Step 4) from client data
      if ((client as any)?.deliveryInfo) {
        try {
          const parsedDelivery = JSON.parse((client as any).deliveryInfo);
          if (Array.isArray(parsedDelivery) && parsedDelivery.length) {
            setDeliveryAddresses(parsedDelivery);
          }
        } catch (e) {
          console.error('[ClientModal] Failed to parse deliveryInfo from client', e);
        }
      }
    }
  }, [client, reset]);

  const createMutation = useMutation({
    mutationFn: async (data: CreateClientRequest) => {
      const response = await clientService.createClient(data);
      const createdClient = (response as any)?.client || response;

      // Create a pending payment for the new client based on the total price (including delivery)
      try {
        const amount = parseFloat(totalPrice) || 0;
        if (createdClient?.id && amount > 0) {
          await paymentService.createPayment({
            clientId: createdClient.id,
            amount,
            currency: 'USD',
            method: 'Pending',
            status: 'PENDING',
            notes: `Initial payment for ${createdClient.firstName} ${createdClient.lastName}`,
          } as any);
        }
      } catch (paymentError) {
        console.error('[ClientModal] Payment creation error:', paymentError);
        // Don't fail the client creation if payment creation fails
      }

      return createdClient;
    },
    onSuccess,
    onError: (e: any) => {
      console.error('[ClientModal] Create client error:', e);
      console.error('[ClientModal] Error response:', e?.response?.data);
      const errorMsg = e?.response?.data?.message || e?.response?.data?.errors?.[0]?.message || e?.message || 'Failed to create client';
      setError(errorMsg);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: UpdateClientRequest) => {
      const response = await clientService.updateClient(client!.id, data);
      const updatedClient = (response as any)?.client || response;

      // After updating the client, update (or create) a payment so that Payments reflect the new total price
      try {
        const amount = parseFloat(totalPrice) || 0;
        if (updatedClient?.id && amount > 0) {
          const paymentsResp = await paymentService.getPayments({
            page: 1,
            limit: 1,
            clientId: updatedClient.id,
            sortBy: 'createdAt',
            sortOrder: 'desc',
          } as any);
          const existingPayment = (paymentsResp as any).payments?.[0];

          if (existingPayment) {
            await paymentService.updatePayment(existingPayment.id, { amount } as any);
          } else {
            await paymentService.createPayment({
              clientId: updatedClient.id,
              amount,
              currency: 'USD',
              method: 'Pending',
              status: 'PENDING',
              notes: `Initial payment for ${updatedClient.firstName} ${updatedClient.lastName}`,
            } as any);
          }
        }
      } catch (paymentError) {
        console.error('[ClientModal] Payment update error:', paymentError);
        // Don't fail the client update if payment update fails
      }

      return updatedClient;
    },
    onSuccess,
    onError: (e: any) => setError(e?.response?.data?.message || e?.response?.data?.errors?.[0]?.message || 'Failed to update client'),
  });

  const onSubmit = (data: CreateClientRequest) => {
    setError(null);
    console.log('[ClientModal] Form submitted with data:', data);

    // Only send fields that the backend expects (based on createClientSchema)
    const payload: any = {
      firstName: data.firstName,
      lastName: data.lastName,
      fatherName: data.fatherName,
      motherFullName: (data as any).motherFullName,
      email: data.email,
      mobilePhone: data.mobilePhone,
      nationalityId: data.nationalityId,
      locationId: data.locationId,
      dietId: data.dietId,
      priceId: (data as any).priceId,
      routeDeliveryId: data.routeDeliveryId,
      delivery: withDelivery,
    };

    // Add optional dateOfBirth if provided
    if ((data as any).dateOfBirth) {
      try {
        payload.dateOfBirth = new Date((data as any).dateOfBirth).toISOString();
      } catch {
        // leave as is; server will validate
      }
    }

    // Add optional numeric fields if provided
    if ((data as any).height !== undefined && (data as any).height !== null) {
      payload.height = Number((data as any).height);
    }
    if ((data as any).weight !== undefined && (data as any).weight !== null) {
      payload.weight = Number((data as any).weight);
    }

    // Add optional string fields if provided
    if ((data as any).gender) payload.gender = (data as any).gender;
    if ((data as any).activityLevel) payload.activityLevel = (data as any).activityLevel;

    // Add optional calculated metrics if the schema supports them
    if ((data as any).bmi !== undefined && (data as any).bmi !== null) {
      payload.bmi = Number((data as any).bmi);
    }
    if ((data as any).ibw !== undefined && (data as any).ibw !== null) {
      payload.ibw = Number((data as any).ibw);
    }
    if ((data as any).ubw !== undefined && (data as any).ubw !== null) {
      payload.ubw = Number((data as any).ubw);
    }
    if ((data as any).calories !== undefined && (data as any).calories !== null) {
      payload.calories = Number((data as any).calories);
    }

    // Add optional text fields if provided
    if ((data as any).medicalConditions) payload.medicalConditions = (data as any).medicalConditions;
    if ((data as any).allergies) payload.allergies = (data as any).allergies;
    if ((data as any).dietaryPreferences) payload.dietaryPreferences = (data as any).dietaryPreferences;

    // Attach addresses if provided (send as simple strings)
    if (addresses && addresses.length) {
      const cleaned = addresses.map(a => (a || '').trim()).filter(Boolean);
      if (cleaned.length) payload.addresses = cleaned;
    }

    // Attach cycles and starting date if provided
    if (startingDate) {
      payload.startingDate = new Date(startingDate).toISOString();
    }
    if (cycles && cycles.length > 0) {
      payload.cycles = JSON.stringify(cycles);
    }

    // Attach delivery information (Step 4) if With Delivery is enabled
    if (withDelivery) {
      try {
        payload.deliveryInfo = JSON.stringify(deliveryAddresses);
      } catch (e) {
        console.error('[ClientModal] Failed to serialize delivery info', e);
      }
    } else if (isEditing) {
      // When editing and With Delivery is turned off, clear any existing delivery info
      payload.deliveryInfo = null;
    }

    console.log('[ClientModal] Sending payload to API:', payload);

    if (isEditing) updateMutation.mutate(payload);
    else createMutation.mutate(payload);
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const allCities: City[] = citiesData?.cities || [];
  const countries: Country[] = countriesData?.countries || [];
  const diets: Diet[] = dietsData?.diets || [];
  const prices: Price[] = pricesData?.prices || [];
  const routeDeliveries = routeDeliveriesData?.routeDeliveries || [];

  // Filter cities based on selected country
  const cities = countryId
    ? allCities.filter(city => (city as any).countryId === countryId)
    : allCities;

  const handleNextStep = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Auto-calculate Total Price when Daily Price, Nb Of Days, or Delivery changes
  useEffect(() => {
    const daily = parseFloat(dailyPrice) || 0;
    const days = nbDays || 0;
    let total = daily * days;

    // Add delivery charges when enabled
    // Delivery Charge is per selected delivery day, NOT per plan day.
    // Example: Daily Price 10 * 30 days = 300
    //          Delivery Charge 10, Days Related = [Mon, Thu, Fri] (3 days)
    //          Delivery total = 3 * 10 = 30, Final = 330
    if (withDelivery) {
      const deliveryTotal = deliveryAddresses.reduce((sum, addr) => {
        const charge = parseFloat(addr.deliveryCharge) || 0;
        const dayCount = (addr.daysRelated && addr.daysRelated.length) || 0;
        return sum + charge * dayCount;
      }, 0);
      total += deliveryTotal;
    }

    setTotalPrice(total.toFixed(2));

    console.log('[ClientModal] Auto-calculating Total Price:');
    console.log('  Daily Price:', daily);
    console.log('  Nb Of Days:', days);
    console.log('  With Delivery:', withDelivery);
    console.log('  Delivery Addresses:', deliveryAddresses);
    console.log('  Total Price:', total.toFixed(2));
  }, [dailyPrice, nbDays, withDelivery, deliveryAddresses]);

  const handleAddCycle = () => {
    if (!startingDate) {
      alert('Please select a Starting Date first');
      return;
    }
    if (!nbDays || nbDays <= 0) {
      alert('Please enter a valid number of days');
      return;
    }

    const newSequence = cycles.length + 1;
    const newCycleNumber = cycles.length + 1;

    // Calculate from and to dates
    const start = new Date(startingDate);
    const previousCycles = cycles.length;
    const daysOffset = previousCycles * nbDays;

    const fromDate = new Date(start);
    fromDate.setDate(fromDate.getDate() + daysOffset);

    const toDate = new Date(fromDate);
    toDate.setDate(toDate.getDate() + nbDays - 1);

    const newCycle: Cycle = {
      id: `cycle-${Date.now()}`,
      sequence: newSequence,
      cycleNumber: newCycleNumber,
      fromDate: fromDate.toISOString().split('T')[0],
      toDate: toDate.toISOString().split('T')[0],
      selected: false,
      recipes: '',
      paid: false,
      freezedNotes: '',
      status: 'Active',
      statusDate: new Date().toISOString(),
    };

    setCycles([...cycles, newCycle]);
  };

  const handleSelectRecipes = (cycle: Cycle) => {
    setSelectedCycleForRecipes(cycle);
    setShowRecipeSelectionModal(true);
  };

  return (
    <>
      <Transition appear show={true} as={React.Fragment}>
        {/* When medical history modal or recipe selection modal is open, prevent the parent dialog from closing on outside clicks */}
        <Dialog as="div" className="relative z-50" onClose={(showMedicalHistoryModal || showRecipeSelectionModal) ? () => {} : onClose}>
        <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className={`fixed inset-0 bg-black bg-opacity-25 ${(showMedicalHistoryModal || showRecipeSelectionModal) ? 'pointer-events-none' : ''}`} />
        </Transition.Child>
        <div className="fixed inset-0 flex items-center justify-center p-0">
          <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
            <Dialog.Panel className="w-screen h-screen bg-white text-left align-middle shadow-xl transition-all flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
                <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">{isEditing ? 'Edit Client' : 'Create Client'}</Dialog.Title>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-6 w-6" /></button>
              </div>

              {/* Step Indicators */}
              <div className="px-6 py-3 flex gap-2 flex-shrink-0 border-b bg-gray-50">
                {STEPS.map((step) => (
                  <div key={step.id} className="flex-1">
                    <button
                      type="button"
                      onClick={() => setCurrentStep(step.id)}
                      className={`w-full py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        currentStep === step.id
                          ? 'bg-blue-500 text-white'
                          : currentStep > step.id
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {step.label}
                    </button>
                  </div>
                ))}
              </div>

              {error && (<div className="mx-6 mt-4 rounded-lg bg-red-50 p-4 flex-shrink-0"><div className="text-sm text-red-700">{error}</div></div>)}
              <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto px-6 py-4">
                  {/* STEP 1: Personal Information */}
                  {currentStep === 1 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Client Personal Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="form-label">First Name *</label>
                          <input {...register('firstName', { required: 'First name is required' })} type="text" className="form-input" />
                          {errors.firstName && <p className="form-error">{errors.firstName.message as string}</p>}
                        </div>
                        <div>
                          <label className="form-label">Father Name</label>
                          <input {...register('fatherName')} type="text" className="form-input" />
                        </div>
                        <div>
                          <label className="form-label">Mother Full Name</label>
                          <input {...register('motherFullName' as any)} type="text" className="form-input" />
                        </div>
                        <div>
                          <label className="form-label">Last Name *</label>
                          <input {...register('lastName', { required: 'Last name is required' })} type="text" className="form-input" />
                          {errors.lastName && <p className="form-error">{errors.lastName.message as string}</p>}
                        </div>
                        <div>
                          <label className="form-label">Gender *</label>
                          <select {...register('gender', { required: 'Gender is required' })} className="form-input">
                            <option value="">Select...</option>
                            <option value="MALE">Male</option>
                            <option value="FEMALE">Female</option>
                            <option value="OTHER">Other</option>
                          </select>
                          {errors.gender && <p className="form-error">{errors.gender.message as string}</p>}
                        </div>
                        <div>
                          <label className="form-label">Date of Birth *</label>
                          <input {...register('dateOfBirth', { required: 'Date of birth is required' })} type="date" className="form-input" />
                          {errors.dateOfBirth && <p className="form-error">{errors.dateOfBirth.message as string}</p>}
                        </div>
                        <div>
                          <label className="form-label">Country *</label>
                          <select {...register('countryId', { required: 'Country is required', onChange: (e) => { setValue('countryId', (e as any).target.value); setValue('locationId', ''); } })} className="form-input">
                            <option value="">Select Country...</option>
                            {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                          {errors.countryId && <p className="form-error">{(errors.countryId as any)?.message as string}</p>}
                        </div>
                        <div>
                          <label className="form-label">Nationality * (Auto-filled)</label>
                          <select {...register('nationalityId', { required: 'Nationality is required' })} className="form-input bg-gray-100" disabled>
                            <option value="">Select...</option>
                            {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                          </select>
                          {errors.nationalityId && <p className="form-error">{errors.nationalityId.message as string}</p>}
                        </div>
                        <div>
                          <label className="form-label">Location *</label>
                          <select {...register('locationId', { required: 'Location is required' })} className="form-input">
                            <option value="">Select Location...</option>
                            {cities.length > 0 ? (
                              cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)
                            ) : (
                              <option disabled>Please select a country first</option>
                            )}
                          </select>
                          {errors.locationId && <p className="form-error">{errors.locationId.message as string}</p>}
                        </div>
                        <div>
                          <label className="form-label">Email *</label>
                          <input {...register('email', { required: 'Email is required', pattern: { value: /[^\s@]+@[^\s@]+\.[^\s@]+/, message: 'Invalid email' } })} type="email" className="form-input" />
                          {errors.email && <p className="form-error">{errors.email.message as string}</p>}
                        </div>
                        <div>
                          <label className="form-label">Mobile Phone *</label>
                          <input {...register('mobilePhone', { required: 'Mobile phone is required' })} type="text" className="form-input" placeholder="e.g., +961 71 123 456" />
                          {errors.mobilePhone && <p className="form-error">{errors.mobilePhone.message as string}</p>}
                        </div>
                        <div>
                          <label className="form-label">Zip Code</label>
                          <input
                            {...register('zipCode')}
                            type="text"
                            className="form-input"
                            placeholder={countriesData?.countries?.find((c: Country) => c.id === countryId)?.zipCodeFormat || 'Enter zip code'}
                          />
                          {errors.zipCode && <p className="form-error">{errors.zipCode.message as string}</p>}
                        </div>
                        <div className="md:col-span-3">
                          <label className="form-label">Client Full Address</label>
                          {addresses.map((addr, idx) => (
                            <div key={idx} className="flex items-center gap-2 mb-2">
                              <input
                                type="text"
                                className="form-input flex-1"
                                placeholder="Full address"
                                value={addr}
                                onChange={(e) => {
                                  const next = [...addresses];
                                  next[idx] = (e as any).target.value;
                                  setAddresses(next);
                                }}
                              />
                              {addresses.length > 1 && (
                                <button
                                  type="button"
                                  className="px-2 py-1 text-sm bg-red-100 hover:bg-red-200 rounded"
                                  onClick={() => {
                                    setAddresses(addresses.filter((_, i) => i !== idx));
                                  }}
                                >
                                  -
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            className="mt-1 px-3 py-1 text-sm bg-emerald-500 hover:bg-emerald-600 text-white rounded"
                            onClick={() => setAddresses([...addresses, ''])}
                          >
                            + Add Address
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 2: Assessment Info */}
                  {currentStep === 2 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Assessment Information</h4>

                      {/* Anthropometric Measurements */}
                      <div className="mb-4">
                        <h5 className="text-xs font-semibold text-gray-700 mb-2 uppercase">Anthropometric Measurements</h5>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                          <div>
                            <label className="form-label">Age (Year) *</label>
                            <input {...register('age', { required: 'Age is required', valueAsNumber: true })} type="number" className="form-input" placeholder="e.g., 30" />
                            {errors.age && <p className="form-error">{errors.age.message as string}</p>}
                          </div>
                          <div>
                            <label className="form-label">Height (cm) *</label>
                            <input {...register('height', { required: 'Height is required', valueAsNumber: true })} type="number" step="0.1" className="form-input" placeholder="e.g., 170" />
                            {errors.height && <p className="form-error">{errors.height.message as string}</p>}
                          </div>
                          <div>
                            <label className="form-label">Weight (Kg) *</label>
                            <input {...register('weight', { required: 'Weight is required', valueAsNumber: true })} type="number" step="0.1" className="form-input" placeholder="e.g., 70" />
                            {errors.weight && <p className="form-error">{errors.weight.message as string}</p>}
                          </div>
                          <div>
                            <label className="form-label">Activity Level *</label>
                            <select {...register('activityLevel', { required: 'Activity level is required' })} className="form-input">
                              <option value="">Select...</option>
                              <option value="SEDENTARY">Sedentary</option>
                              <option value="LIGHT">Light Activity</option>
                              <option value="MODERATE">Moderate Activity</option>
                              <option value="ACTIVE">Active</option>
                              <option value="VERY_ACTIVE">Very Active</option>
                            </select>
                            {errors.activityLevel && <p className="form-error">{errors.activityLevel.message as string}</p>}
                          </div>
                        </div>
                      </div>

                      {/* Calculated Metrics */}
                      {(height || weight) && (
                        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mb-4">
                          <h5 className="text-xs font-semibold text-gray-700 mb-2 uppercase">Calculated Metrics</h5>
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                            <div>
                              <label className="text-xs font-medium text-gray-600">BMI (Kg/m²) *</label>
                              <p className="text-lg font-semibold text-blue-600">{watch('bmi' as any) || '-'}</p>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-gray-600">IBW (Kg) *</label>
                              <p className="text-lg font-semibold text-blue-600">{watch('ibw' as any) || '-'}</p>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-gray-600">UBW (Kg) *</label>
                              <p className="text-lg font-semibold text-blue-600">{watch('ubw' as any) || '-'}</p>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-gray-600">Calories (KCal) *</label>
                              <p className="text-lg font-semibold text-blue-600">{watch('calories' as any) || '-'}</p>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-gray-600">BMI Status</label>
                              <input {...register('bmiStatus')} type="text" className="form-input text-sm" placeholder="Normal" />
                            </div>
                          </div>
                          <div className="mt-2">
                            <label className="form-label text-xs">Weight Change</label>
                            <input {...register('weightChange')} type="number" step="0.1" className="form-input" placeholder="e.g., 5" />
                          </div>
                        </div>
                      )}

                      {/* Biological Data */}
                      <div className="mb-4">
                        <h5 className="text-xs font-semibold text-gray-700 mb-2 uppercase">Biological Data</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="form-label">Medical History</label>
                              <button
                                type="button"
                                onClick={() => setShowMedicalHistoryModal(true)}
                                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                              >
                                Manage
                              </button>
                            </div>
                            {/* Display a human-friendly summary; keep the JSON value in a hidden input */}
                            <textarea
                              className="form-input bg-gray-50"
                              rows={2}
                              placeholder="No conditions selected. Click Manage."
                              value={medicalHistorySummary || ''}
                              readOnly
                            />
                            <input type="hidden" {...register('medicalHistory')} />
                          </div>
                          <div>
                            <label className="form-label">Medications</label>
                            <textarea {...register('medications')} className="form-input" rows={2} placeholder="Current medications..." />
                          </div>
                          <div>
                            <label className="form-label">Vitamin Deficiencies</label>
                            <textarea {...register('vitaminDeficiencies')} className="form-input" rows={2} placeholder="Vitamin deficiencies..." />
                          </div>
                          <div>
                            <label className="form-label">Counter Medications</label>
                            <textarea {...register('counterMedications')} className="form-input" rows={2} placeholder="Counter medications..." />
                          </div>
                        </div>
                      </div>

                      {/* Diet History */}
                      <div className="mb-4">
                        <h5 className="text-xs font-semibold text-gray-700 mb-2 uppercase">Diet History</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className="form-label">Previous Diet</label>
                            <input {...register('previousDiet')} type="text" className="form-input" placeholder="Previous diet..." />
                          </div>
                          <div>
                            <label className="form-label">Last Diet Date</label>
                            <input {...register('lastDietDate')} type="date" className="form-input" />
                          </div>
                          <div>
                            <label className="form-label">Weight Lost</label>
                            <input {...register('weightLost')} type="number" step="0.1" className="form-input" placeholder="e.g., 5" />
                          </div>
                        </div>
                      </div>

                      {/* General Assessment */}
                      <div className="mb-4">
                        <h5 className="text-xs font-semibold text-gray-700 mb-2 uppercase">General Assessment</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="flex items-center gap-3">
                            <input {...register('smoking')} type="checkbox" className="form-checkbox" id="smoking" />
                            <label htmlFor="smoking" className="form-label mb-0">Smoking</label>
                          </div>
                          <div className="flex items-center gap-3">
                            <input {...register('alcohol')} type="checkbox" className="form-checkbox" id="alcohol" />
                            <label htmlFor="alcohol" className="form-label mb-0">Alcohol</label>
                          </div>
                          <div>
                            <label className="form-label">Allergies History</label>
                            <select {...register('allergiesHistory')} multiple className="form-input" style={{ minHeight: '100px' }}>
                              <option value="">Select allergies...</option>
                              {(ingredientsData?.ingredients || []).map(ingredient => (
                                <option key={ingredient.id} value={ingredient.name}>{ingredient.name}</option>
                              ))}
                            </select>
                            <small className="text-gray-500">Hold Ctrl/Cmd to select multiple</small>
                          </div>
                          <div>
                            <label className="form-label">Food Dislike</label>
                            <select {...register('foodDislike')} multiple className="form-input" style={{ minHeight: '100px' }}>
                              <option value="">Select foods...</option>
                              {(ingredientsData?.ingredients || []).map(ingredient => (
                                <option key={ingredient.id} value={ingredient.name}>{ingredient.name}</option>
                              ))}
                            </select>
                            <small className="text-gray-500">Hold Ctrl/Cmd to select multiple</small>
                          </div>
                          <div className="flex items-center gap-3">
                            <input {...register('eatingDisorder')} type="checkbox" className="form-checkbox" id="eatingDisorder" />
                            <label htmlFor="eatingDisorder" className="form-label mb-0">Eating Disorder</label>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 3: Diet & Menu */}
                  {currentStep === 3 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Diet & Menu</h4>

                      {/* Top controls row */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end mb-3">
                        <div>
                          <label className="form-label">Current Diet *</label>
                          <div className="flex gap-2">
                            <select {...register('dietId', { required: 'Diet is required' })} className="form-input flex-1">
                              <option value="">Select...</option>
                              {diets.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                            <button
                              type="button"
                              className="px-3 py-2 text-sm bg-gray-100 rounded hover:bg-gray-200"
                              onClick={() => {
                                const id = watch('dietId' as any);
                                if (id) window.open(`/setup/diets/${id}/menu-details`, '_blank');
                              }}
                            >
                              View Menu Details
                            </button>
                          </div>
                          {errors.dietId && <p className="form-error">{errors.dietId.message as string}</p>}
                        </div>

                        <div>
                          <label className="form-label">Price *</label>
                          <select {...register('priceId' as any)} className="form-input">
                            <option value="">Select</option>
                            {prices.map(p => (
                              <option key={p.id} value={p.id}>{p.name} ({p.amount} {p.currency})</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="form-label">Nb Of Days</label>
                          <div className="flex gap-2 items-center">
                            <input type="number" className="form-input" placeholder="30" value={nbDays} onChange={(e) => setNbDays(Number(e.target.value) || 0)} />
                            <button type="button" className="px-3 py-2 text-sm bg-gray-100 rounded hover:bg-gray-200" onClick={() => { /* no-op refresh */ }}>
                              Refresh
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Secondary controls */}
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                        <label className="inline-flex items-center gap-2">
                          <input type="checkbox" className="form-checkbox" checked={fullPackage} onChange={(e) => setFullPackage(e.target.checked)} />
                          <span className="text-sm text-gray-700">Full Package</span>
                        </label>
                        <div>
                          <label className="form-label">Daily Price</label>
                          <input type="number" step="0.01" className="form-input" value={dailyPrice} onChange={(e) => setDailyPrice(e.target.value)} />
                        </div>
                        <div>
                          <label className="form-label">Starting Date</label>
                          <input type="date" className="form-input" value={startingDate} onChange={(e) => setStartingDate(e.target.value)} />
                        </div>
                      </div>

                      {/* Cycles header */}
                      <div className="mt-6 rounded-lg border overflow-hidden">
                        <div className="bg-slate-700 text-white px-3 py-2 flex items-center justify-between">
                          <span className="font-semibold">Cycles</span>
                          <button
                            type="button"
                            className="px-3 py-1 text-sm bg-emerald-500 hover:bg-emerald-600 rounded"
                            onClick={handleAddCycle}
                          >
                            + New Cycle
                          </button>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead>
                              <tr className="bg-gray-100 text-gray-700">
                                <th className="px-3 py-2 text-left">Sequence</th>
                                <th className="px-3 py-2 text-left">Cycle #</th>
                                <th className="px-3 py-2 text-left">From</th>
                                <th className="px-3 py-2 text-left">To</th>
                                <th className="px-3 py-2 text-left">Selected</th>
                                <th className="px-3 py-2 text-left">Recipes</th>
                                <th className="px-3 py-2 text-left">Paid</th>
                                <th className="px-3 py-2 text-left">Freezed Notes</th>
                                <th className="px-3 py-2 text-left">Status</th>
                                <th className="px-3 py-2 text-left">Status Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              {cycles.length === 0 ? (
                                <tr>
                                  <td colSpan={10} className="px-3 py-6 text-center text-gray-500">No Records</td>
                                </tr>
                              ) : (
                                cycles.map((cycle) => (
                                  <tr key={cycle.id} className="border-t hover:bg-gray-50">
                                    <td className="px-3 py-2">{cycle.sequence}</td>
                                    <td className="px-3 py-2">{cycle.cycleNumber}</td>
                                    <td className="px-3 py-2">{cycle.fromDate}</td>
                                    <td className="px-3 py-2">{cycle.toDate}</td>
                                    <td className="px-3 py-2">
                                      <input
                                        type="checkbox"
                                        checked={cycle.selected}
                                        onChange={(e) => {
                                          setCycles(cycles.map(c =>
                                            c.id === cycle.id ? { ...c, selected: e.target.checked } : c
                                          ));
                                        }}
                                      />
                                    </td>
                                    <td className="px-3 py-2">
                                      <button
                                        type="button"
                                        className="text-blue-600 hover:underline text-xs"
                                        onClick={() => handleSelectRecipes(cycle)}
                                      >
                                        Select Recipes
                                      </button>
                                    </td>
                                    <td className="px-3 py-2">
                                      <input
                                        type="checkbox"
                                        checked={cycle.paid}
                                        onChange={(e) => {
                                          setCycles(cycles.map(c =>
                                            c.id === cycle.id ? { ...c, paid: e.target.checked } : c
                                          ));
                                        }}
                                      />
                                    </td>
                                    <td className="px-3 py-2">
                                      <input
                                        type="text"
                                        className="form-input text-xs"
                                        value={cycle.freezedNotes}
                                        onChange={(e) => {
                                          setCycles(cycles.map(c =>
                                            c.id === cycle.id ? { ...c, freezedNotes: e.target.value } : c
                                          ));
                                        }}
                                      />
                                    </td>
                                    <td className="px-3 py-2">
                                      <select
                                        className="form-input text-xs"
                                        value={cycle.status}
                                        onChange={(e) => {
                                          setCycles(cycles.map(c =>
                                            c.id === cycle.id ? { ...c, status: e.target.value as 'Active' | 'Inactive' } : c
                                          ));
                                        }}
                                      >
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                      </select>
                                    </td>
                                    <td className="px-3 py-2">{new Date(cycle.statusDate).toLocaleString()}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 4: Payments */}
                  {currentStep === 4 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-3">Payment Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="form-label">Current Diet</label>
                          <input type="text" className="form-input bg-gray-50" disabled value={watch('dietId' as any) ? diets.find(d => d.id === watch('dietId' as any))?.name : ''} />
                        </div>
                        <div>
                          <label className="form-label">Total Price</label>
                          <input
                            type="text"
                            className="form-input bg-gray-50 font-semibold text-lg"
                            disabled
                            value={totalPrice}
                          />
                        </div>
                        <div>
                          <label className="form-label">Currency</label>
                          <select className="form-input">
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                            <option value="LBP">LBP</option>
                          </select>
                        </div>
                        <div>
                          <label className="form-label">Payment Option</label>
                          <input type="text" className="form-input" placeholder="Payment Option" />
                        </div>
                        <div>
                          <label className="form-label">Nb Of Days</label>
                          <input
                            type="number"
                            className="form-input bg-gray-50"
                            disabled
                            value={nbDays}
                          />
                        </div>
                        <div className="flex items-center space-x-4">
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              className="form-checkbox"
                              checked={withDelivery}
                              onChange={(e) => setWithDelivery(e.target.checked)}
                            />
                            <span className="text-sm text-gray-700">With Delivery</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              className="form-checkbox"
                              checked={withTax}
                              onChange={(e) => setWithTax(e.target.checked)}
                            />
                            <span className="text-sm text-gray-700">With Tax</span>
                          </label>
                        </div>
                      </div>

                      {/* Delivery Information Section */}
                      {withDelivery && (
                        <div className="mt-4">
                          {deliveryAddresses.map((address, index) => (
                            <div key={index} className="mb-4 p-3 border border-gray-300 rounded-lg bg-gray-50">
                              <h5 className="text-sm font-semibold text-gray-900 mb-3">
                                Delivery Info Address {index + 1}
                              </h5>
                              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                <div>
                                  <label className="form-label">
                                    Address Type <span className="text-red-500">*</span>
                                  </label>
                                  <select
                                    className="form-input"
                                    value={address.addressType}
                                    onChange={(e) => {
                                      const newAddresses = [...deliveryAddresses];
                                      newAddresses[index].addressType = e.target.value;
                                      setDeliveryAddresses(newAddresses);
                                    }}
                                  >
                                    <option value="">Select</option>
                                    <option value="Home">Home</option>
                                    <option value="Office">Office</option>
                                    <option value="Other">Other</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="form-label">
                                    Address Preferable <span className="text-red-500">*</span>
                                  </label>
                                  <select
                                    className="form-input"
                                    value={address.addressPreference}
                                    onChange={(e) => {
                                      const newAddresses = [...deliveryAddresses];
                                      newAddresses[index].addressPreference = e.target.value;
                                      setDeliveryAddresses(newAddresses);
                                    }}
                                  >
                                    <option value="">Select</option>
                                    <option value="Primary">Primary</option>
                                    <option value="Secondary">Secondary</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="form-label">
                                    Location <span className="text-red-500">*</span>
                                  </label>
                                  <select
                                    className="form-input"
                                    value={address.location}
                                    onChange={(e) => {
                                      const newAddresses = [...deliveryAddresses];
                                      newAddresses[index].location = e.target.value;
                                      setDeliveryAddresses(newAddresses);
                                    }}
                                  >
                                    <option value="">Select</option>
                                    {cities.map(city => (
                                      <option key={city.id} value={city.name}>{city.name}</option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label className="form-label">
                                    Delivery Charge (USD / Per Day) <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    className="form-input"
                                    placeholder="0.00"
                                    value={address.deliveryCharge}
                                    onChange={(e) => {
                                      const newAddresses = [...deliveryAddresses];
                                      newAddresses[index].deliveryCharge = e.target.value;
                                      setDeliveryAddresses(newAddresses);
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="form-label">
                                    Days Related <span className="text-red-500">*</span>
                                  </label>
                                  <div className="space-y-1">
                                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                                      <label key={day} className="flex items-center space-x-2 text-sm">
                                        <input
                                          type="checkbox"
                                          className="form-checkbox"
                                          checked={address.daysRelated.includes(day)}
                                          onChange={(e) => {
                                            const newAddresses = [...deliveryAddresses];
                                            const current = newAddresses[index].daysRelated || [];
                                            if (e.target.checked) {
                                              newAddresses[index].daysRelated = [...current, day];
                                            } else {
                                              newAddresses[index].daysRelated = current.filter((d) => d !== day);
                                            }
                                            setDeliveryAddresses(newAddresses);
                                          }}
                                        />
                                        <span>{day}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                                <div>
                                  <label className="form-label">
                                    Route Delivery <span className="text-red-500">*</span>
                                  </label>
                                  <select
                                    className="form-input"
                                    value={address.routeDelivery}
                                    onChange={(e) => {
                                      const newAddresses = [...deliveryAddresses];
                                      newAddresses[index].routeDelivery = e.target.value;
                                      setDeliveryAddresses(newAddresses);
                                    }}
                                  >
                                    <option value="">Select</option>
                                    {routeDeliveries.map(route => (
                                      <option key={route.id} value={route.id}>
                                        {route.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label className="form-label">
                                    Time <span className="text-red-500">*</span>
                                  </label>
                                  <select
                                    className="form-input"
                                    value={address.time}
                                    onChange={(e) => {
                                      const newAddresses = [...deliveryAddresses];
                                      newAddresses[index].time = e.target.value;
                                      setDeliveryAddresses(newAddresses);
                                    }}
                                  >
                                    <option value="">Select</option>
                                    <option value="AM">AM</option>
                                    <option value="PM">PM</option>
                                    <option value="Very Late PM">Very Late PM</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="form-label">Reference</label>
                                  <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Reference"
                                    value={address.reference}
                                    onChange={(e) => {
                                      const newAddresses = [...deliveryAddresses];
                                      newAddresses[index].reference = e.target.value;
                                      setDeliveryAddresses(newAddresses);
                                    }}
                                  />
                                </div>
                                <div className="md:col-span-4">
                                  <label className="form-label">When No One In Home</label>
                                  <input
                                    type="text"
                                    className="form-input"
                                    placeholder="When No One In Home"
                                    value={address.whenNoOneInHome}
                                    onChange={(e) => {
                                      const newAddresses = [...deliveryAddresses];
                                      newAddresses[index].whenNoOneInHome = e.target.value;
                                      setDeliveryAddresses(newAddresses);
                                    }}
                                  />
                                </div>
                                <div>
                                  <label className="form-label">Delivered By</label>
                                  <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Delivered By"
                                    value={address.deliveredBy}
                                    onChange={(e) => {
                                      const newAddresses = [...deliveryAddresses];
                                      newAddresses[index].deliveredBy = e.target.value;
                                      setDeliveryAddresses(newAddresses);
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => {
                              setDeliveryAddresses([...deliveryAddresses, {
                                addressType: '',
                                addressPreference: '',
                                location: '',
                                deliveryCharge: '0.00',
                                daysRelated: [],
                                routeDelivery: '',
                                time: '',
                                whenNoOneInHome: '',
                                reference: '',
                                deliveredBy: '',
                              }]);
                            }}
                          >
                            Add More
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                </form>

                {/* Navigation Buttons - Sticky Footer */}
                <div className="flex justify-between items-center px-6 py-3 border-t flex-shrink-0 bg-white">
                  <div className="flex space-x-3">
                    {currentStep > 1 && (
                      <Button type="button" variant="secondary" onClick={handlePrevStep} disabled={isLoading}>
                        <ChevronLeftIcon className="h-4 w-4 mr-1" />
                        Previous
                      </Button>
                    )}
                  </div>
                  <div className="flex space-x-3">
                    <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>Cancel</Button>
                    {currentStep < STEPS.length ? (
                      <Button type="button" onClick={handleNextStep} disabled={isLoading}>
                        Next
                        <ChevronRightIcon className="h-4 w-4 ml-1" />
                      </Button>
                    ) : (
                      <Button type="submit" loading={isLoading} onClick={handleSubmit(onSubmit)}>{isEditing ? 'Update' : 'Create'}</Button>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>

      {/* Medical History Modal */}
      <MedicalHistoryModal
        isOpen={showMedicalHistoryModal}
        onClose={() => setShowMedicalHistoryModal(false)}
        onSave={(medicalHistory) => {
          setValue('medicalHistory', medicalHistory);
          setShowMedicalHistoryModal(false);
        }}
        initialValue={watch('medicalHistory')}
      />

      {/* Recipe Selection Modal */}
      {showRecipeSelectionModal && selectedCycleForRecipes && (
        <RecipeSelectionModal
          isOpen={showRecipeSelectionModal}
          dietId={watch('dietId' as any) || ''}
          onClose={() => {
            setShowRecipeSelectionModal(false);
            setSelectedCycleForRecipes(null);
          }}
          onSave={(selectedRecipes) => {
            // Update the cycle with selected recipes
            setCycles(cycles.map(c =>
              c.id === selectedCycleForRecipes.id
                ? { ...c, recipes: JSON.stringify(selectedRecipes) }
                : c
            ));
            setShowRecipeSelectionModal(false);
            setSelectedCycleForRecipes(null);
          }}
          initialSelections={selectedCycleForRecipes.recipes ? JSON.parse(selectedCycleForRecipes.recipes) : {}}
        />
      )}
    </>
  );
};

export default ClientModal;

